import React, {
  ChangeEvent,
  KeyboardEvent,
  FormEvent,
  FC,
  useRef,
  useState,
  useMemo,
  useContext,
  useImperativeHandle,
} from "react";
import { defineMessages, useIntl } from "react-intl";
import { sanitize } from "dompurify";
import classNames from "classnames";
import Svg from "@natera/material/lib/svg";
import { APIContext, EventsContext } from "@app/pippa/provider";
import { KeyTypes } from "@app/pippa/models/constants";
import { isEmptyString } from "@app/pippa/helpers";
import ArrowUp from "@app/pippa/assets/svg/arrow-up.svg";
import "./userInput.scss";

export const MAX_INPUT_LENGTH = 2200;
const EXTERNAL_MARGINS = 32;
const GAP_VALUE = 10;
const MAX_HEIGHT = 160;

const messages = defineMessages({
  textAreaPlaceholder: {
    id: "textAreaPlaceholder",
    defaultMessage: "Ask me anything",
  },
  disabledInputPlaceholder: {
    id: "disabledInputPlaceholder",
    defaultMessage: "Select an option to continue",
  },
  feedbackInputPlaceholder: {
    id: "feedbackInputPlaceholder",
    defaultMessage: "Rate using buttons above",
  },
});

const UserInput: FC = () => {
  const intl = useIntl();
  const {
    userEventHandler,
    textMessageRef,
    userInputRef,
    eventsProviderState,
  } = useContext(EventsContext);
  const {
    apiProviderState: { chatId },
  } = useContext(APIContext);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hiddenSpanRef = useRef<HTMLSpanElement>(null);
  const userInputContainerRef = useRef<HTMLDivElement>(null);
  const actionContainerRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState("");
  const isButtonDisabled = useMemo(() => !text.trim(), [text]);
  const isInputDisabled = eventsProviderState.isInputDisabled;
  const lastAssistantMessage = [...eventsProviderState.messages]
    .reverse()
    .find((m) => !m.isSendByUser);
  const isFeedbackActive =
    isInputDisabled && lastAssistantMessage?.feedback != null;
  const placeholder = isInputDisabled
    ? intl.formatMessage(
        isFeedbackActive
          ? messages.feedbackInputPlaceholder
          : messages.disabledInputPlaceholder
      )
    : intl.formatMessage(messages.textAreaPlaceholder);

  const handleSend = () => {
    if (!isEmptyString(text)) {
      const preparedText = sanitize(text).replace(/\s+/g, " ").trim();
      userEventHandler(preparedText, chatId);
      textMessageRef?.current?.disableRating();
      setText("");
      resetTextareaStyles();
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value.slice(0, MAX_INPUT_LENGTH));
  };

  const handleEnter = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    const isEnter = e.key.toLowerCase() === KeyTypes.ENTER;
    const isShift = e.shiftKey;

    if (isEnter && isShift) return;
    if (isEnter) {
      e.preventDefault();
      handleSend();
    }
  };

  const resetTextareaStyles = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "20px";
      textareaRef.current.style.width = "auto";
      textareaRef.current.style.marginBottom = "18px";
      textareaRef.current.style.overflowY = "hidden";
    }
  };

  useImperativeHandle(userInputRef, () => ({
    clearInput() {
      setText("");
      resetTextareaStyles();
    },
  }));

  const onInputHandler = (e: FormEvent<HTMLTextAreaElement>) => {
    const textarea = e.target as HTMLTextAreaElement;

    textarea.style.height = "20px";
    textarea.style.width = "auto";

    requestAnimationFrame(() => {
      const userInputContainerWidth =
        userInputContainerRef.current?.clientWidth || 0;
      const actionContainerWidth = actionContainerRef.current?.clientWidth || 0;
      const hiddenSpanWidth =
        (hiddenSpanRef.current?.clientWidth || 0) + GAP_VALUE;

      const isOverflowing =
        hiddenSpanWidth + actionContainerWidth >
        userInputContainerWidth - EXTERNAL_MARGINS;
      const availableTextareaWidth =
        userInputContainerWidth - actionContainerWidth - EXTERNAL_MARGINS;

      if (isOverflowing) {
        textarea.style.width = "100%";
        textarea.style.height = `${textarea.scrollHeight}px`;
      } else {
        textarea.style.width = `${availableTextareaWidth}px`;
      }

      textarea.style.marginBottom = `${isOverflowing ? 8 : 18}px`;
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_HEIGHT)}px`;
      textarea.style.overflowY =
        textarea.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
    });
  };

  return (
    <div className="user-input-wrapper">
      <div
        ref={userInputContainerRef}
        className={classNames("user-input-container", {
          "user-input-container--disabled": isInputDisabled,
        })}
      >
        <span ref={hiddenSpanRef} className="user-input__hidden-span">
          {text || " "}
        </span>
        <textarea
          ref={textareaRef}
          className="user-input-textarea"
          id="messaging-user-input"
          autoComplete="off"
          placeholder={placeholder}
          value={text}
          onChange={handleChange}
          onKeyDown={handleEnter}
          onInput={onInputHandler}
          disabled={isInputDisabled}
          data-testid="input"
        />
        <div ref={actionContainerRef} className="user-input__actions-container">
          <div
            className={classNames("user-input__length-indicator", {
              show: text.length >= 200,
              warning: text.length >= MAX_INPUT_LENGTH,
            })}
            data-testid="indicator"
          >
            {text.length}/{MAX_INPUT_LENGTH}
          </div>
          <button
            className="user-input__send-button"
            onClick={handleSend}
            disabled={isButtonDisabled}
          >
            <div className="icon-container">
              <Svg content={ArrowUp} />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserInput;
